import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { artifactUrl, fetchArtifact, useCancelRun, useDeleteRun, useFlights, useRun, useRuns, useSubmitRun } from "../../api/hooks";
import type { ArtifactDict, FlightManifest, RunRecord } from "../../api/types";
import { useSession } from "../../app/store";
import { Split } from "../../components/Split";
import { Empty, JsonTree, Spinner, StatusDot, errText, fmtBytes, fmtDate, fmtDuration, fmtTick } from "../../components/ui";
import "./runs.css";

export function RunsPage() {
  const session = useSession();
  const { projectId } = session;
  const runs = useRuns(projectId);
  const flights = useFlights();
  const submit = useSubmitRun();
  const cancel = useCancelRun();
  const del = useDeleteRun();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string | null>(session.currentRunId);
  const run = useRun(selected);
  const [flightId, setFlightId] = useState<string | null>(null);
  const [tab, setTab] = useState<"summary" | "flight" | "log">("summary");
  const [deliverTo, setDeliverTo] = useState("");
  const [deliver, setDeliver] = useState(false);
  const [trace, setTrace] = useState<"off" | "bool" | "all">("bool");

  useEffect(() => {
    if (!selected && runs.data?.length) setSelected(runs.data[0].id);
  }, [runs.data, selected]);
  const manifest = run.data?.manifest ?? null;
  useEffect(() => {
    if (manifest && (!flightId || !manifest.flights.some((f) => f.flight_id === flightId))) setFlightId(manifest.flights[0]?.flight_id ?? null);
  }, [manifest, flightId]);

  const grid = useMemo(() => {
    if (!manifest) return null;
    const triggers = [...new Set(manifest.flights.flatMap((f) => f.triggers))];
    return { triggers, rows: manifest.flights };
  }, [manifest]);

  const start = async () => {
    if (!projectId || !session.selectedFlights.length) {
      session.toast("Select flights on the Flights page first", "error");
      return;
    }
    try {
      const r = await submit.mutateAsync({
        project: projectId,
        flights: session.selectedFlights,
        trace,
        deliver_to: deliverTo.trim() || undefined,
        deliver,
        label: `batch ${session.selectedFlights.length}`,
      });
      session.setCurrentRun(r.id);
      setSelected(r.id);
      session.setDock(true, "runlog");
    } catch (e) {
      session.toast(errText(e), "error");
    }
  };

  if (!projectId) return <Empty title="Select a project" />;
  const fl = manifest?.flights.find((f) => f.flight_id === flightId) ?? null;

  return (
    <Split id="runs" className="runs-page" sizes={[320, "1fr"]} minSizes={[200, 360]}>
      <div className="pane">
        <div className="pane-head">
          Runs
          <span className="spacer" />
          <span className="dim small">{runs.data?.length ?? 0}</span>
        </div>
        <div className="col" style={{ padding: 8, borderBottom: "1px solid var(--rs-border)", gap: 6 }}>
          <div className="row">
            <span className="small dim">
              {session.selectedFlights.length} flight{session.selectedFlights.length === 1 ? "" : "s"} selected
            </span>
            <span className="spacer" />
            <select className="inp sm" value={trace} onChange={(e) => setTrace(e.target.value as typeof trace)} title="Evaluation trace">
              <option value="off">no trace</option>
              <option value="bool">trace: booleans</option>
              <option value="all">trace: all nodes</option>
            </select>
          </div>
          <div className="row">
            <input className="inp sm" placeholder="deliver to folder (optional server path)" value={deliverTo} onChange={(e) => setDeliverTo(e.target.value)} style={{ flex: 1 }} />
            <button className="btn sm primary" onClick={start} disabled={submit.isPending}>
              {submit.isPending ? <Spinner /> : "▶ Run batch"}
            </button>
          </div>
          <label className="row small" title="Route generated reports through the project's subscriptions.yaml (email / SFTP / FTP / webhook / folder sinks)">
            <input type="checkbox" checked={deliver} onChange={(e) => setDeliver(e.target.checked)} />
            deliver via subscriptions
          </label>
        </div>
        <div className="list">
          {(runs.data ?? []).map((r) => (
            <div key={r.id} className={`list-item run-item${r.id === selected ? " sel" : ""}`} onClick={() => (setSelected(r.id), session.setCurrentRun(r.id))}>
              <StatusDot status={r.status} />
              <div className="name">
                <div className="row">
                  <span className="mono">{r.label || r.id}</span>
                  <span className="faint small">{r.mode}</span>
                </div>
                <div className="small dim">
                  {fmtDate(r.created_at)} · {r.flight_ids.length} fl ·{" "}
                  {r.status === "running" || r.status === "queued" ? `${r.progress.done}/${r.progress.total}` : `${r.summary?.fires ?? 0} fires · ${r.summary?.outputs ?? 0} out`}
                  {r.summary?.errors ? <span className="badge err">{r.summary.errors} err</span> : null}
                </div>
              </div>
            </div>
          ))}
          {runs.isLoading ? <Spinner label="loading" /> : null}
          {!runs.isLoading && !runs.data?.length ? <div className="dim small" style={{ padding: 10 }}>No runs yet.</div> : null}
        </div>
      </div>

      {!run.data ? (
        <Empty title="No run selected" />
      ) : (
        <div className="pane">
          <RunHeader run={run.data} onCancel={() => cancel.mutate(run.data!.id)} onDelete={async () => {
            if (!confirm("Delete this run and its artifacts?")) return;
            await del.mutateAsync(run.data!.id);
            setSelected(null);
          }} />
          <div className="tabs">
            <button className={tab === "summary" ? "on" : ""} onClick={() => setTab("summary")}>
              Summary
            </button>
            <button className={tab === "flight" ? "on" : ""} onClick={() => setTab("flight")}>
              Flight {fl ? <span className="badge">{fl.flight_id}</span> : null}
            </button>
            <button className={tab === "log" ? "on" : ""} onClick={() => setTab("log")}>
              Log <span className="badge">{run.data.log.length}</span>
            </button>
          </div>
          <div className="pane-body" style={{ overflow: "auto" }}>
            {tab === "summary" ? (
              grid ? (
                <table className="tbl grid">
                  <thead>
                    <tr>
                      <th>Flight</th>
                      <th>Tail</th>
                      {grid.triggers.map((t) => (
                        <th key={t} className="mono">
                          {t}
                        </th>
                      ))}
                      <th>Outputs</th>
                      <th>Errors</th>
                      <th>Time</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {grid.rows.map((f) => (
                      <tr key={f.flight_id} className={`clickable${f.flight_id === flightId ? " sel" : ""}`} onClick={() => (setFlightId(f.flight_id), setTab("flight"))}>
                        <td className="mono">{f.flight_id}</td>
                        <td>{f.tail ?? flights.data?.find((x) => x.id === f.flight_id)?.tail ?? ""}</td>
                        {grid.triggers.map((t) => {
                          const fires = f.fires.filter((s) => s.trigger === t);
                          const err = f.trigger_errors.filter((e) => e.trigger === t).length;
                          const evs = f.events.filter((e) => e.trigger === t && e.kind === "not_fired");
                          return (
                            <td key={t} className="cell">
                              {fires.map((s) => (
                                <span key={s.id} className="badge ok" title={`${s.label}: window ${fmtTick(s.window.start)}–${fmtTick(s.window.end)} · emitted at ${fmtTick(s.t)}`}>
                                  {s.label} {fmtTick(s.window.start)}
                                  {s.window.end > s.window.start ? `–${fmtTick(s.window.end)}` : ""}
                                </span>
                              ))}
                              {err ? <span className="badge err">{err} error</span> : null}
                              {!fires.length && !err ? <span className="faint">{evs.length ? "not fired" : "—"}</span> : null}
                            </td>
                          );
                        })}
                        <td className="num">{f.outputs.length}</td>
                        <td className="num">{f.errors.length + f.render_errors.length + f.trigger_errors.length || ""}</td>
                        <td className="num">{f.elapsed_s.toFixed(1)}s</td>
                        <td>
                          <button
                            className="btn ghost sm"
                            title="Open in Analysis"
                            onClick={(e) => {
                              e.stopPropagation();
                              session.setActiveFlight(f.flight_id);
                              session.setCurrentRun(run.data!.id);
                              navigate("/analysis");
                            }}
                          >
                            📈
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : run.data.status === "failed" ? (
                <div className="problem err" style={{ margin: 10 }}>
                  {run.data.error}
                </div>
              ) : (
                <Spinner label={`${run.data.status} ${run.data.progress.done}/${run.data.progress.total}`} />
              )
            ) : null}
            {tab === "flight" ? fl ? <FlightDetail run={run.data} fl={fl} /> : <Empty title="No flight" /> : null}
            {tab === "log" ? (
              <pre className="out" style={{ margin: 10 }}>
                {run.data.log.join("\n")}
              </pre>
            ) : null}
          </div>
        </div>
      )}
    </Split>
  );
}

function RunHeader({ run, onCancel, onDelete }: { run: RunRecord; onCancel: () => void; onDelete: () => void }) {
  const dur =
    run.started_at && run.finished_at ? (new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()) / 1000 : null;
  return (
    <div className="pane-head row wrap">
      <StatusDot status={run.status} />
      <span className="mono">{run.id}</span>
      {run.label ? <span className="badge">{run.label}</span> : null}
      <span className="dim small">
        {fmtDate(run.created_at)} · {run.user ?? ""} · {fmtDuration(dur)}
      </span>
      {run.summary ? (
        <span className="small">
          <span className="badge">{run.summary.flights} flights</span> <span className="badge ok">{run.summary.fires} fires</span>{" "}
          <span className="badge">{run.summary.outputs} outputs</span>{" "}
          {run.summary.errors ? <span className="badge err">{run.summary.errors} errors</span> : null}
        </span>
      ) : null}
      {run.options.source_override ? <span className="badge warn" title="ran with unsaved editor source">scratch</span> : null}
      {run.manifest?.out_dir ? (
        <span className="mono small dim" title="artifact folder">
          {run.manifest.out_dir}
        </span>
      ) : null}
      <span className="spacer" />
      {run.status === "running" || run.status === "queued" ? (
        <button className="btn sm" onClick={onCancel}>
          Cancel
        </button>
      ) : (
        <button className="btn sm danger" onClick={onDelete}>
          Delete
        </button>
      )}
    </div>
  );
}

function FlightDetail({ run, fl }: { run: RunRecord; fl: FlightManifest }) {
  const [art, setArt] = useState<ArtifactDict | null>(null);
  const [text, setText] = useState<string>("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setArt(null);
    setText("");
  }, [fl.flight_id, run.id]);
  const open = async (a: ArtifactDict) => {
    setArt(a);
    setBusy(true);
    try {
      setText(await fetchArtifact(run.id, fl.flight_id, a.filename));
    } catch (e) {
      setText(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Split id="runs-flight" sizes={["2fr", "3fr"]} minSizes={[260, 300]} collapsible={[true, false]}>
      <div className="col" style={{ padding: 10, overflow: "auto", gap: 12 }}>
        <div>
          <div className="section-title">Snapshots ({fl.fires.length})</div>
          {fl.fires.length ? (
            <table className="tbl">
              <thead>
                <tr>
                  <th>Trigger</th>
                  <th>Label</th>
                  <th>Window</th>
                  <th title="tick at which the trigger emitted the snapshot (end of flight for end-of-flight triggers)">Emitted</th>
                  <th>Payload</th>
                </tr>
              </thead>
              <tbody>
                {fl.fires.map((s) => (
                  <tr key={s.id}>
                    <td className="mono">{s.trigger}</td>
                    <td>{s.label}</td>
                    <td className="num">
                      {fmtTick(s.window.start)}–{fmtTick(s.window.end)}
                    </td>
                    <td className="num dim">{fmtTick(s.t)}</td>
                    <td>
                      {Object.keys(s.values).length ? (
                        <details>
                          <summary className="small dim">{Object.keys(s.values).length} keys</summary>
                          <JsonTree value={s.values} />
                        </details>
                      ) : (
                        <span className="faint">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="dim small">No trigger fired on this flight.</div>
          )}
        </div>
        <div>
          <div className="section-title">Artifacts ({fl.outputs.length})</div>
          {fl.outputs.map((a, i) => (
            <div key={i} className={`list-item${art === a ? " sel" : ""}`} onClick={() => open(a)}>
              <span className="name mono">{a.filename}</span>
              <span className="badge">{a.report}</span>
              <span className="faint small">{fmtBytes(a.size)}</span>
              <a className="btn ghost sm" href={artifactUrl(run.id, fl.flight_id, a.filename, true)} onClick={(e) => e.stopPropagation()} title="download">
                ⤓
              </a>
            </div>
          ))}
          {!fl.outputs.length ? <div className="dim small">No artifacts.</div> : null}
        </div>
        {fl.skipped.length ? (
          <div>
            <div className="section-title">Skipped ({fl.skipped.length})</div>
            {fl.skipped.map((s, i) => (
              <div key={i} className="small dim">
                {s.snapshot} {s.report ? `→ ${s.report}` : ""}: {s.reason}
              </div>
            ))}
          </div>
        ) : null}
        {fl.trigger_errors.length || fl.render_errors.length || fl.errors.length ? (
          <div>
            <div className="section-title">Errors</div>
            {fl.trigger_errors.map((e, i) => (
              <details key={`t${i}`} className="problem err">
                <summary>
                  {e.trigger} ({e.phase} @ {fmtTick(e.t)}): {e.error}
                </summary>
                <pre className="mono small">{e.traceback}</pre>
              </details>
            ))}
            {fl.render_errors.map((e, i) => (
              <details key={`r${i}`} className="problem err">
                <summary>
                  {e.report}/{e.output} ({e.snapshot}): {e.error}
                </summary>
                <pre className="mono small">{e.traceback}</pre>
              </details>
            ))}
            {fl.errors.map((e, i) => (
              <div key={`e${i}`} className="problem err">
                {e}
              </div>
            ))}
          </div>
        ) : null}
        {fl.events.length ? (
          <div>
            <div className="section-title">Events ({fl.events.length})</div>
            {fl.events.map((e, i) => (
              <div key={i} className="small">
                <span className="dim mono">{fmtTick(e.t)}</span> <span className="badge">{e.kind}</span> {e.trigger}: {e.message}
              </div>
            ))}
          </div>
        ) : null}
        {fl.flight ? (
          <div>
            <div className="section-title">Flight fields</div>
            <JsonTree value={fl.flight} />
          </div>
        ) : null}
      </div>
      <div className="col" style={{ minHeight: 0, padding: 10 }}>
        {art ? (
          <>
            <div className="row">
              <span className="mono">{art.filename}</span>
              <span className="badge">{art.media_type}</span>
              <span className="dim small">
                {art.report}/{art.output} · {art.trigger}/{art.label}
              </span>
              <span className="spacer" />
              <a className="btn sm" href={artifactUrl(run.id, fl.flight_id, art.filename, true)}>
                Download
              </a>
            </div>
            {busy ? <Spinner /> : <pre className="artifact mono">{typeof text === "string" ? text : JSON.stringify(text, null, 2)}</pre>}
          </>
        ) : (
          <Empty title="Pick an artifact to view it" />
        )}
      </div>
    </Split>
  );
}
